import { BarChartMulti } from '@mnd-frontend/charts';
import { BrandReportGroupStatisticsBy } from '@mnd-frontend/graphql-schema';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { Report2VisibilityMentionsOverTimeFragment } from '../../../../generated/types';
import {
  useReportDynamicData,
  useReportEditable,
  useReportGraphHelpers,
  useReportStaticData,
  useReportTracking,
  useReportTranslation,
} from '../../hooks';
import { GraphCaption, HitsModal, type HitsModalDetails, SkeletonBarChart } from '../common';

import { subYears } from 'date-fns';
import qs from 'qs';

import { useNavigate } from '@mnd-frontend/ui';
import { GraphContainer } from '../common/GraphContainer';
import { ScrollContainer } from '../common/ScrollContainer';

export const MentionsOverTimeGraph = ({
  mentionsOverTime,
  groupBy,
  loading,
}: {
  mentionsOverTime: Report2VisibilityMentionsOverTimeFragment['mentionsOverTime'];
  groupBy: BrandReportGroupStatisticsBy;
  loading: boolean;
}) => {
  const { compareToPreviousYear, fromDate, toDate, searchProfiles, mediaTypes } =
    useReportStaticData();
  const { monitorLink } = useReportEditable();
  const navigate = useNavigate();
  const { getLegendCaption, getPeriodLabel, getPeriodLabelLong } = useReportGraphHelpers();
  const legendCaptionCurrentYear = getLegendCaption({ fromDate, toDate });
  const legendCaptionPreviousYear = getLegendCaption({
    fromDate: subYears(fromDate, 1),
    toDate: subYears(toDate, 1),
  });
  const { brandColor } = useReportDynamicData();

  const { data, mappings } = useMemo(() => {
    const mappings: {
      name: string | ReactNode;
      dataKey: 'mentions' | 'mentionsPreviousYear';
      color: string;
      detailsDataKey: 'details' | 'detailsPreviousYear';
    }[] = [
      {
        name: legendCaptionCurrentYear,
        dataKey: 'mentions',
        detailsDataKey: 'details',
        color: brandColor,
      },
    ];
    if (compareToPreviousYear)
      mappings.unshift({
        name: legendCaptionPreviousYear,
        dataKey: 'mentionsPreviousYear',
        detailsDataKey: 'detailsPreviousYear',
        color: brandColor + '80',
      });
    return {
      data: mentionsOverTime.map(period => {
        const year = new Date(period.startAt).getFullYear();
        return {
          name: getPeriodLabel(groupBy, year, period.month, period.week),
          mentions: period.mentions,
          month: period.month,
          mentionsPreviousYear: period.mentionsPreviousYear,
          details: {
            startAt: period.startAt,
            endAt: period.endAt,
            label: getPeriodLabelLong(groupBy, year, period.month, period.week),
          },
          detailsPreviousYear: {
            startAt: period.startAtPreviousYear,
            endAt: period.endAtPreviousYear,
            label: getPeriodLabelLong(groupBy, year - 1, period.month, period.week),
          },
        };
      }),
      mappings,
    };
  }, [
    legendCaptionCurrentYear,
    brandColor,
    compareToPreviousYear,
    legendCaptionPreviousYear,
    mentionsOverTime,
    getPeriodLabel,
    groupBy,
    getPeriodLabelLong,
  ]);

  const onLinkClick = monitorLink
    ? (details?: HitsModalDetails) => {
        if (!details) return;
        const { startAt, endAt } = details;

        navigate({
          pathname: monitorLink,
          search: qs.stringify({
            time: 'range',
            from: startAt,
            to: endAt,
            profile: searchProfiles.map(s => s.id)?.join(','),
            source: mediaTypes?.map(mt => mt.toLowerCase()).join(','),
          }),
        });
      }
    : undefined;
  const { t } = useReportTranslation();
  const { track } = useReportTracking();
  const [details, setDetails] = useState<HitsModalDetails | null>(null);
  const onBarClick = useCallback(
    ({ details }: { details: HitsModalDetails }) => {
      track({
        event: 'Brand Reports: Clicked Mentions Over Time graph',
      });
      setDetails(details);
    },
    [track],
  );
  const onModalClose = () => setDetails(null);
  const showMonths = groupBy === BrandReportGroupStatisticsBy.Month;

  return (
    <ScrollContainer minWidth="35rem">
      <GraphContainer data-testid="mentions-graph">
        {!loading && (
          <>
            <GraphCaption>{t('reports_number_of_mentions')}</GraphCaption>
            <BarChartMulti
              height={450}
              data={data}
              legend={compareToPreviousYear}
              mappings={mappings}
              showMonths={showMonths}
              onBarClick={onBarClick}
            />
          </>
        )}
        {loading && <SkeletonBarChart />}
        {details ? (
          <HitsModal
            onLinkClick={onLinkClick ? () => onLinkClick(details) : undefined}
            onClose={onModalClose}
            details={details}
          />
        ) : null}
      </GraphContainer>
    </ScrollContainer>
  );
};
